"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useCart } from '../hooks/useCart';
import type { CartItem } from './shopping/types';
import { API_BASE_URL, getImageUrl } from '@/app/lib/api';
import {
  ShoppingCart as LucideShoppingCart, X, Minus, Plus,
  Trash2, Package, ArrowRight, ArrowLeft,
  Sparkles, Truck, Shield, ShoppingBag, AlertCircle
} from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';

// --- CONSTANTS ---
const FREE_SHIPPING_THRESHOLD = 300;
const SHIPPING_COST = 9.000;

// --- UTILS ---
const vibrate = (pattern: number | number[]) => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const nav = navigator as any;
  try { nav.vibrate?.(pattern); } catch (e) { }
};

// --- SUB-COMPONENTS ---

/**
 * Barre de progression pour la livraison gratuite
 */
const ShippingProgressBar = ({ totalPrice }: { totalPrice: number }) => {
  const progress = Math.min((totalPrice / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const remaining = FREE_SHIPPING_THRESHOLD - totalPrice;

  return (
    <div className="px-4 py-4 bg-emerald-50/50 border-y border-emerald-100">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Truck className={`w-5 h-5 ${totalPrice >= FREE_SHIPPING_THRESHOLD ? 'text-emerald-600' : 'text-gray-400'}`} />
          <p className="text-sm font-medium">
            {totalPrice >= FREE_SHIPPING_THRESHOLD ? (
              <span className="text-emerald-700 font-bold">LIVRAISON GRATUITE OFFERTE ! 🎉</span>
            ) : (
              <span className="text-gray-700">
                Plus que <b className="text-emerald-600">{remaining.toFixed(3)} DT</b> pour la livraison gratuite
              </span>
            )}
          </p>
        </div>
        <span className="text-xs font-bold text-emerald-700 bg-white px-2 py-0.5 rounded-full border border-emerald-200">
          {Math.round(progress)}%
        </span>
      </div>
      <div className="relative h-2.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-linear-to-r from-emerald-400 to-emerald-600 transition-all duration-1000 ease-out"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute inset-0 bg-size-[20px_20px] bg-[linear-gradient(45deg,rgba(255,255,255,0.2)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.2)_50%,rgba(255,255,255,0.2)_75%,transparent_75%,transparent)] animate-[shimmer_2s_linear_infinite]" />
        </div>
      </div>
    </div>
  );
};

const QuantitySelector = ({
  quantity,
  onIncrease,
  onDecrease
}: {
  quantity: number;
  onIncrease: () => void;
  onDecrease: () => void;
}) => (
  <div className="flex items-center bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <button
      onClick={onDecrease}
      disabled={quantity <= 1}
      className="p-2 text-gray-500 hover:bg-gray-50 disabled:opacity-30 transition-colors"
    >
      <Minus size={16} strokeWidth={3} />
    </button>
    <span className="w-8 text-center font-bold text-gray-800 text-sm">{quantity}</span>
    <button
      onClick={onIncrease}
      className="p-2 text-gray-500 hover:bg-gray-50 transition-colors"
    >
      <Plus size={16} strokeWidth={3} />
    </button>
  </div>
);

const CartItemCard = ({
  item,
  onIncrease,
  onDecrease,
  onRemove
}: {
  item: CartItem;
  onIncrease: (id: string) => void;
  onDecrease: (id: string) => void;
  onRemove: (id: string) => void;
}) => {
  const price = Number(item.price);
  const oldPrice = item.oldPrice ? Number(item.oldPrice) : null;
  const isPromo = oldPrice && oldPrice > price;

  return (
    <div className="flex gap-3 p-3 sm:p-4 bg-white border-b border-gray-100 last:border-0 group animate-in fade-in slide-in-from-right-4 duration-300">
      {/* Image Container */}
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-white rounded-xl overflow-hidden border border-gray-100">
        <Image
          src={item.image ? getImageUrl(item.image) : '/images/placeholder.webp'}
          alt={item.name}
          fill
          className="object-contain p-2"
          sizes="96px"
        />
        {isPromo && (
          <div className="absolute top-1 left-1 bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded shadow-sm">
            PROMO
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex justify-between items-start gap-2">
          <Link
            href={`/produit/${item.slug || item.id}`}
            className="text-sm sm:text-base font-semibold text-gray-900 line-clamp-2 hover:text-emerald-600 transition-colors leading-tight"
          >
            {item.name}
          </Link>
          <button
            onClick={() => onRemove(String(item.id))}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all flex-shrink-0"
          >
            <Trash2 size={16} />
          </button>
        </div>

        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-base font-bold text-gray-900">{price.toFixed(3)} DT</span>
          {isPromo && (
            <span className="text-xs text-gray-400 line-through">{oldPrice?.toFixed(3)} DT</span>
          )}
        </div>

        <div className="mt-auto flex items-center justify-between">
          <QuantitySelector
            quantity={item.quantity}
            onIncrease={() => onIncrease(String(item.id))}
            onDecrease={() => onDecrease(String(item.id))}
          />
          <div className="text-right">
            <span className="text-sm font-bold text-gray-900">
              {(price * item.quantity).toFixed(3)} DT
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const RecommendedProducts = ({ productId }: { productId?: string }) => {
  const [products, setProducts] = useState<RecommendedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const base = API_BASE_URL.replace(/\/$/, '');
      const recommendedUrl = productId
        ? `${base}/products/recommended/${encodeURIComponent(productId)}`
        : `${base}/products`;
      const fallbackUrl = `${base}/products`;

      let rawProducts: ApiProduct[] = [];

      const recommendedRes = await fetch(recommendedUrl, { cache: 'no-store' });
      if (recommendedRes.ok) {
        const recommendedData = await recommendedRes.json();
        rawProducts = getProductsFromApiPayload(recommendedData);
      }

      if (rawProducts.length === 0) {
        const fallbackRes = await fetch(fallbackUrl, { cache: 'no-store' });
        if (fallbackRes.ok) {
          const fallbackData = await fallbackRes.json();
          rawProducts = getProductsFromApiPayload(fallbackData);
        }
      }

      const normalized = rawProducts
        .map((item) => {
          const price = Number(item.price ?? item.prixPromo ?? 0);
          const image = Array.isArray(item.images) ? item.images[0] : item.image;

          return {
            id: String(item.id ?? ''),
            name: item.name || item.title || 'Produit',
            price: Number.isFinite(price) ? price : 0,
            image: image || '/images/placeholder.webp',
            slug: String(item.slug || item.id || ''),
          };
        })
        .filter((item) => item.id && item.slug)
        .filter((item) => !productId || item.id !== productId)
        .slice(0, 6);

      setProducts(normalized);
    } catch (e) {
      console.error(e);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  if (loading || products.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-amber-500 fill-amber-500" />
        <h3 className="font-bold text-sm uppercase tracking-wider text-gray-700">Vous aimerez aussi</h3>
      </div>

      <div className="relative group/swiper">
        <Swiper
          modules={[Navigation]}
          spaceBetween={12}
          slidesPerView={2.2}
          navigation={{
            nextEl: '.rec-next',
            prevEl: '.rec-prev',
          }}
          breakpoints={{ 640: { slidesPerView: 3 } }}
        >
          {products.map(product => (
            <SwiperSlide key={product.id}>
              <div className="bg-white p-2 rounded-xl border border-gray-100 h-full flex flex-col">
                <Link href={`/produit/${product.slug}`} className="relative aspect-square mb-2 block">
                  <Image src={product.image ? getImageUrl(product.image) : '/images/placeholder.webp'} alt={product.name} fill className="object-contain" sizes="120px" />
                </Link>
                <p className="text-[11px] font-medium line-clamp-2 mb-1 h-8 leading-tight">{product.name}</p>
                <p className="text-xs font-bold mb-2">{product.price.toFixed(3)} DT</p>
                <button
                  onClick={() => addToCart({ ...product, quantity: 1 })}
                  className="mt-auto w-full py-1.5 bg-main text-white hover:bg-main hover:text-white text-[10px] font-bold rounded-lg transition-colors uppercase"
                >
                  Ajouter
                </button>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <button className="rec-prev absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-lg p-1 rounded-full -ml-2 opacity-0 group-hover/swiper:opacity-100 transition-opacity">
          <ArrowLeft size={16} />
        </button>
        <button className="rec-next absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-lg p-1 rounded-full -mr-2 opacity-0 group-hover/swiper:opacity-100 transition-opacity">
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

const CartPanel = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { cartItems, handleIncrease, handleDecrease, handleRemoveItem, totalPrice } = useCart();
  const shippingCost = totalPrice >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_COST;
  const finalTotal = totalPrice + shippingCost;

  // Empêcher le scroll du body quand le panier est ouvert
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <div className={`fixed inset-0 z-[100] transition-all duration-300 ${isOpen ? 'visible' : 'invisible'}`}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`absolute right-0 top-0 h-full w-full max-w-full sm:max-w-md bg-white shadow-2xl transform transition-transform duration-500 ease-in-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-emerald-600" />
            <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Mon Panier</h2>
            <span className="ml-2 bg-gray-100 text-gray-600 text-xs font-bold px-2 py-1 rounded-full">
              {cartItems.length}
            </span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><X size={24} /></button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50/30">
          {cartItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center p-8 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <Package className="w-12 h-12 text-gray-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Votre panier est vide</h3>
              <p className="text-gray-500 mb-8">Découvrez nos produits et commencez votre shopping !</p>
              <button onClick={onClose} className="px-8 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-emerald-600 transition-colors">
                Retour à la boutique
              </button>
            </div>
          ) : (
            <>
               <div className="bg-white">
                {cartItems.map(item => (
                  <CartItemCard
                    key={item.id}
                    item={item}
                    onIncrease={handleIncrease}
                    onDecrease={handleDecrease}
                    onRemove={handleRemoveItem}
                  />
                ))}
              </div>
              <div className="p-4">
                <RecommendedProducts productId={cartItems[0]?.id?.toString()} />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="bg-white border-t p-4 space-y-3 shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
            <div className="space-y-2 px-1">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Sous-total</span>
                <span>{totalPrice.toFixed(3)} DT</span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Livraison</span>
                <span className={shippingCost === 0 ? "text-emerald-600 font-bold" : ""}>
                  {shippingCost === 0 ? "GRATUITE" : `${shippingCost.toFixed(3)} DT`}
                </span>
              </div>
              <div className="flex justify-between items-end pt-2 border-t text-gray-900">
                <span className="text-base font-bold uppercase">Total</span>
                <span className="text-2xl font-black">{finalTotal.toFixed(3)} DT</span>
              </div>
            </div>

            {totalPrice < 20 && (
              <div className="bg-red-50 text-red-600 text-xs font-semibold p-3 rounded-lg border border-red-100 flex items-start gap-2 mb-2">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0 text-red-500" />
                <span>Le montant minimum de commande est de <b>20,000 DT</b>. Ajoutez encore <b>{(20 - totalPrice).toFixed(3)} DT</b> pour commander.</span>
              </div>
            )}

            {totalPrice < 20 ? (
              <button
                disabled
                className="flex items-center justify-center w-full py-4 bg-gray-200 text-gray-400 font-bold rounded-xl cursor-not-allowed border border-gray-300"
              >
                Commander maintenant
                <ArrowRight size={20} className="ml-2" />
              </button>
            ) : (
              <Link
                href="/checkout"
                onClick={onClose}
                className="group relative flex items-center justify-center w-full py-4 bg-main hover:bg-emerald-700 text-white font-bold rounded-xl transition-all active:scale-[0.98] overflow-hidden shadow-lg shadow-emerald-100"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Commander maintenant
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            )}

            <div className="flex items-center justify-center gap-4 text-[10px] text-gray-400 font-medium uppercase tracking-widest">
              <div className="flex items-center gap-1"><Shield size={12} /> Paiement 100% sécurisé</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---
export default function ShoppingCart() {
  const { cartItems, totalPrice } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const prevCount = useRef(cartItems.length);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (cartItems.length > prevCount.current) {
      setIsOpen(true);
      setShouldAnimate(true);
      vibrate(50);
      const timer = setTimeout(() => setShouldAnimate(false), 1000);
      return () => clearTimeout(timer);
    }
    prevCount.current = cartItems.length;
  }, [cartItems.length]);

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener('open-cart', handleOpen);
    return () => window.removeEventListener('open-cart', handleOpen);
  }, []);

  return (
    <>
      {/* Header Cart Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="relative p-2.5 bg-main hover:bg-gray-100 rounded-full transition-all group"
      >
        <LucideShoppingCart className="w-6 h-6 text-white group-hover:text-emerald-600 transition-colors" />
        {isMounted && cartItems.length > 0 && (
          <span className={`absolute -top-1 -right-1 bg-emerald-600 text-white text-[10px] font-black rounded-full min-w-5 h-5 px-1 flex items-center justify-center border-2 border-white shadow-sm ${shouldAnimate ? 'animate-bounce' : ''}`}>
            {cartItems.length}
          </span>
        )}
      </button>

      {/* Floating Cart Button (Desktop only, visible if cart not empty and drawer closed) */}
      {isMounted && cartItems.length > 0 && !isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-48 right-6 z-90 hidden md:flex items-center gap-3 pl-4 pr-6 py-3 bg-main text-white rounded-full shadow-2xl hover:bg-emerald-600 transition-all hover:-translate-y-1 group animate-in slide-in-from-bottom-10"
        >
          <div className="relative">
            <ShoppingBag size={24} />
            <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-gray-900">
              {cartItems.length}
            </span>
          </div>
          <div className="flex flex-col items-start leading-none">
            <span className="text-[10px] font-bold text-gray-400 uppercase">Mon Panier</span>
            <span className="text-sm font-black">{totalPrice.toFixed(3)} DT</span>
          </div>
        </button>
      )}

      <CartPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}

// Type definitions
type RecommendedProduct = {
  id: string;
  name: string;
  price: number;
  image: string;
  slug: string;
};

type ApiProduct = {
  id?: string | number;
  name?: string;
  title?: string;
  price?: number | string;
  prixPromo?: number | string;
  images?: string[];
  image?: string;
  slug?: string;
};

const getProductsFromApiPayload = (payload: any): ApiProduct[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.products)) return payload.products;
  return [];
};